import axios, { AxiosResponse } from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IRebateSetting, defaultValue } from 'app/shared/model/rebate-setting.model';

const initialState: EntityState<IRebateSetting> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/rebate-settings';

// The global axios interceptor resolves (rather than rejects) failed requests, so without this a
// 4xx/5xx would "fulfill" the thunk with no data and push undefined into `entities`.
const requireData = <T>(result: AxiosResponse<T>): AxiosResponse<T> => {
  if (!result?.data) {
    throw result;
  }
  return result;
};

// Actions

export const getEntitiesByBank = createAsyncThunk(
  'rebateSetting/fetch_entity_list_by_bank',
  async ({ bankId, commodityType }: { bankId: number | string; commodityType: string }) => {
    const requestUrl = `${apiUrl}/by-bank?bankId=${bankId}&commodityType=${commodityType}`;
    return requireData(await axios.get<IRebateSetting[]>(requestUrl));
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'rebateSetting/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IRebateSetting>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'rebateSetting/create_entity',
  async (entity: { bankId: number | string; commodityType: string; name: string }) => {
    const result = requireData(await axios.post<IRebateSetting>(apiUrl, entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const duplicateEntity = createAsyncThunk(
  'rebateSetting/duplicate_entity',
  async ({ id, name }: { id: number | string; name: string }) => {
    const result = requireData(await axios.post<IRebateSetting>(`${apiUrl}/${id}/duplicate`, { name }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const renameEntity = createAsyncThunk(
  'rebateSetting/rename_entity',
  async ({ id, name }: { id: number | string; name: string }) => {
    const result = requireData(await axios.patch<IRebateSetting>(`${apiUrl}/${id}/rename`, { name }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const activateEntity = createAsyncThunk(
  'rebateSetting/activate_entity',
  async (id: number | string) => {
    const result = requireData(await axios.patch<IRebateSetting>(`${apiUrl}/${id}/activate`));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deactivateEntity = createAsyncThunk(
  'rebateSetting/deactivate_entity',
  async (id: number | string) => {
    const result = requireData(await axios.patch<IRebateSetting>(`${apiUrl}/${id}/deactivate`));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateConfiguration = createAsyncThunk(
  'rebateSetting/update_configuration',
  async ({ id, ...configuration }: { id: number | string; [key: string]: any }) => {
    const result = requireData(await axios.put<IRebateSetting>(`${apiUrl}/${id}/configuration`, configuration));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'rebateSetting/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const RebateSettingSlice = createEntitySlice({
  name: 'rebateSetting',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = state.entities.filter((e: IRebateSetting) => e.id !== action.meta.arg);
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntitiesByBank), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, duplicateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
        state.entities = [...state.entities, action.payload.data];
      })
      .addMatcher(isFulfilled(renameEntity, activateEntity, deactivateEntity, updateConfiguration), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
        state.entities = state.entities.map((e: IRebateSetting) => (e.id === action.payload.data.id ? action.payload.data : e));
      })
      .addMatcher(isPending(getEntitiesByBank, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(createEntity, duplicateEntity, renameEntity, activateEntity, deactivateEntity, updateConfiguration, deleteEntity),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = RebateSettingSlice.actions;

// Reducer
export default RebateSettingSlice.reducer;
